/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { IErrorModel } from "../../../../models";
import { authStorage, sessionManager } from "../../../../services";

export const HttpRequestFulfilledInterceptor = async (
  config: InternalAxiosRequestConfig,
  country: string
): Promise<InternalAxiosRequestConfig> => {
  const session = authStorage.getSessionBehavior();

  if (config?.headers?.hasOwnProperty("Authorization")) {
    const authorization = config.headers["Authorization"];
    // Delete empty Authorization header
    if (!authorization) {
      delete config.headers["Authorization"];
      delete config.headers["X-REDBEE-AUTH"];
    }
  } else {
    if (!config.headers) {
      config.headers = new axios.AxiosHeaders();
    }

    if (session && session.DataToken) {
      config.headers["Authorization"] = `Bearer ${session.Token}`;
      config.headers["X-REDBEE-AUTH"] = `Bearer ${session.DataToken}`;
    }
  }

  if (!config.headers["X-NF-Content-Country"]) {
    config.headers["X-NF-Content-Country"] = country.toUpperCase();
  }

  if (!config.headers["Content-Type"]) {
    config.headers["Content-Type"] = "application/json; charset=UTF-8";
  }

  return Promise.resolve(config);
};

export const HttpRequestRejectedInterceptor = (
  error: IErrorModel
): Promise<any> => {
  return Promise.reject(error);
};

export const HttpResponseFulfilledInterceptor = (
  response: AxiosResponse
): AxiosResponse => response;

export const HttpResponseRejectedInterceptor = async (
  error: IErrorModel | any
): Promise<any> => {
  const { config, response } = error;
  const originalRequest = config;

  if (!originalRequest || originalRequest._retry) {
    return Promise.reject(error);
  }

  if (!response || response.status === 401) {
    // Before the session has been loaded there is nothing to refresh
    if (!authStorage.getSessionBehavior()?.RefreshToken) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const session = await sessionManager.refresh();

      originalRequest.headers["Authorization"] = `Bearer ${session.Token}`;
      originalRequest.headers["X-REDBEE-AUTH"] = `Bearer ${session.DataToken}`;

      return axios(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }

  return Promise.reject(error);
};
